<script lang="ts">
	/* eslint-disable @typescript-eslint/no-confusing-void-expression */
	import commarecShot from "@assets/images/case-study/commarec-web.png?webp&w=1280&imagetools";
	import { Background } from "@components/Background.js";
	import Section from "@components/Section.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import Media from "@components/common/Media.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import arrowNext from "@core/assets/icons/arrow-next.svg?raw";
	import bracketsIcon from "@core/assets/icons/brackets.svg?raw";
	import codeIcon from "@core/assets/icons/code.svg?raw";
	import databaseIcon from "@core/assets/icons/database.svg?raw";
	import devicesIcon from "@core/assets/icons/devices.svg?raw";
	import envelopeIcon from "@core/assets/icons/envelope.svg?raw";
	import gitIcon from "@core/assets/icons/git.svg?raw";
	import graphIcon from "@core/assets/icons/graph.svg?raw";
	import headphonesIcon from "@core/assets/icons/headphones.svg?raw";
	import integrationIcon from "@core/assets/icons/integration.svg?raw";
	import phoneIcon from "@core/assets/icons/phone.svg?raw";
	import quoteIcon from "@core/assets/icons/quote.svg?raw";
	import { getConnectWithFastestPath } from "@core/schema/paths/getConnectWithFastestPath.js";
	import { getDigitalizationAndAutomationPath } from "@core/schema/paths/getDigitalizationAndAutomationPath.js";
	import { getIntegrationsPath } from "@core/schema/paths/getIntegrationsPath.js";
	import { getTransportPath } from "@core/schema/paths/getTransportPath.js";
	import { getWebAppsPath } from "@core/schema/paths/getWebAppsPath.js";
	import { getWebsitesPath } from "@core/schema/paths/getWebsitesPath.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { BookMeetingPerson, getBookMeetingPeople } from "../data/getBookMeetingPeople.js";
	import type { CaseStudyCommarecProps } from "./CaseStudyCommarecProps.js";

	const _: CaseStudyCommarecProps = $props();
	const { tenant } = getGlobalTemplateProps();
	const contacts = getBookMeetingPeople(tenant);
	const martin = contacts[BookMeetingPerson.Kucera];

	const challenges = [
		{
			label: "Platforma",
			name: "Platforma místo prezentace",
			desc: "Web měl fungovat jako obsahový ekosystém — odborné články, případovky, podcasty, produkty i inspirace z oboru.",
			icon: databaseIcon,
			featured: true,
		},
		{
			label: "Design",
			name: "Neortodoxní design",
			desc: "Výrazný a odlišný design netypický pro logistiku. Vizuální identita stejně inovativní jako sama firma.",
			icon: codeIcon,
			featured: false,
		},
		{
			label: "Budoucnost",
			name: "Flexibilita do budoucna",
			desc: "Připravenost na další rozvoj — nové typy obsahu, rozšiřování katalogu řešení a další digitální produkty.",
			icon: gitIcon,
			featured: false,
		},
	];

	const cmsFeatures = [
		{ icon: codeIcon, label: "Články a blog" },
		{ icon: graphIcon, label: "Případové studie" },
		{ icon: headphonesIcon, label: "Podcasty" },
		{ icon: integrationIcon, label: "Katalog produktů a řešení" },
		{ icon: databaseIcon, label: "Obsah pro komunitu" },
		{ icon: devicesIcon, label: "Inspirace z oboru" },
	];

	const technologies = ["AWS", "DynamoDB", "AppSync", "Svelte", "Tailwind", "GraphQL"];

	const bannerLinks = [
		{ label: "Vývoj webových aplikací", href: getWebAppsPath(tenant) },
		{ label: "Web na míru", href: getWebsitesPath(tenant) },
		{ label: "Integrace systémů", href: getIntegrationsPath(tenant) },
		{ label: "Digitalizace a automatizace", href: getDigitalizationAndAutomationPath(tenant) },
	];

	let showStickyCta = $state(false);
	$effect((): (() => void) => {
		const onScroll = (): void => {
			const max = document.documentElement.scrollHeight - window.innerHeight;
			showStickyCta = window.scrollY > 700 && window.scrollY < max - 700;
		};
		onScroll();
		window.addEventListener("scroll", onScroll, { passive: true });
		return () => {
			window.removeEventListener("scroll", onScroll);
		};
	});
</script>

<!-- Velká CTA karta -->
{#snippet ctaBig(icon: string, eyebrow: string, heading: string, desc: string, btnText: string, href: string)}
	<div class="container-12">
		<div
			class="flex flex-col gap-8 rounded-[1.25rem] bg-white p-8 shadow-[0_8px_30px_rgba(33,32,32,0.06)] md:flex-row md:items-center md:gap-10 md:p-11"
		>
			<div class="flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-[18px] bg-fstgreen">
				<Icon {icon} class="w-9 text-fstonyx" square />
			</div>
			<div class="flex-1">
				<p class="!mb-1 text-base font-medium text-fstgreen">{eyebrow}</p>
				<h3 class="!mb-2 !mt-0 text-md font-black text-fstonyx md:text-2md">{heading}</h3>
				<p class="!mb-0 text-base text-fstgrey-200">{desc}</p>
			</div>
			<Link variant={ButtonVariant.Phlox} class="flex shrink-0 items-center justify-center sm:!w-auto" {href}>
				{btnText}
				<Icon icon={arrowNext} class="ml-2 shrink-0 text-fstgrey-900"></Icon>
			</Link>
		</div>
	</div>
{/snippet}

<!-- Inline CTA karta -->
{#snippet ctaInline(icon: string, heading: string, desc: string, linkText: string, href: string)}
	<div class="container-12">
		<div
			class="flex flex-col gap-6 rounded-[1.25rem] border-l-4 border-fstgreen bg-white p-6 shadow-[0_8px_30px_rgba(33,32,32,0.04)] md:flex-row md:items-center md:gap-10 md:px-10 md:py-7"
		>
			<div class="flex h-12 w-12 shrink-0 items-center justify-center text-fstgreen">
				<Icon {icon} class="w-8" square />
			</div>
			<div class="flex-1">
				<h3 class="!mb-1 !mt-0 text-base font-bold text-fstonyx md:text-md">{heading}</h3>
				<p class="!mb-0 text-sm text-fstgrey-200 md:text-base">{desc}</p>
			</div>
			<a
				{href}
				class="flex shrink-0 items-center gap-2 font-medium text-fstgreen transition-colors hover:text-fstonyx md:whitespace-nowrap"
			>
				{linkText}
				<Icon icon={arrowNext} class="w-5 shrink-0"></Icon>
			</a>
		</div>
	</div>
{/snippet}

<Layout>
	<!-- Hero -->
	<Section hero background={Background.LightGradient} noPadding="top">
		<div class="container-12 grid grid-cols-1 items-center gap-16 pt-14 lg:grid-cols-2 lg:gap-20">
			<div>
				<span class="mb-6 block font-loos-normal text-xs font-medium uppercase tracking-[0.34em] text-fstgrey-200">
					Případová studie · Commarec
				</span>
				<h1 class="!mb-6">
					Jak jsme pomohli vytvořit <span class="text-fstgreen">logistickou platformu</span>, která přesahuje běžný
					firemní web
				</h1>
				<p class="mb-8 max-w-xl text-md leading-[1.5em] text-fstgrey-200 lg:text-2md">
					Jak jsme pro Commarec postavili digitální platformu na míru — obsahový hub pro logistický obor s vlastním CMS,
					který může dlouhodobě růst spolu s firmou.
				</p>
				<div class="mb-10 flex flex-col gap-4 sm:flex-row">
					<Link
						variant={ButtonVariant.Phlox}
						class="flex items-center justify-center sm:!w-auto"
						href={getConnectWithFastestPath(tenant)}
					>
						Chci podobnou platformu
						<Icon icon={arrowNext} class="ml-2 shrink-0 text-fstgrey-900"></Icon>
					</Link>
					<Link
						variant={ButtonVariant.GreenOutlineLightBg}
						class="flex items-center justify-center sm:!w-auto"
						href="#pribeh"
					>
						Přečíst příběh
					</Link>
				</div>
				<ul class="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-x-6">
					{#each ["Vlastní CMS bez technických omezení", "Obsahový hub pro celý obor", "Připraveno na další rozvoj"] as point (point)}
						<li
							class="flex items-center gap-2 text-sm text-fstgrey-200 before:h-1.5 before:w-1.5 before:shrink-0 before:rounded-full before:bg-fstgreen before:content-['']"
						>
							{point}
						</li>
					{/each}
				</ul>
			</div>

			<div class="relative mx-auto hidden w-full max-w-xl lg:block">
				<div class="overflow-hidden rounded-[1.25rem] bg-white shadow-[0_20px_60px_rgba(33,32,32,0.12)]">
					<div class="flex items-center gap-2 border-b border-fstlight-200 px-4 py-3">
						<span class="h-3 w-3 rounded-full bg-fstlight-300"></span>
						<span class="h-3 w-3 rounded-full bg-fstlight-300"></span>
						<span class="h-3 w-3 rounded-full bg-fstlight-300"></span>
						<span class="ml-3 flex-1 rounded-full bg-fstlight-100 px-4 py-1 text-xs text-fstgrey-300">commarec.cz</span>
					</div>
					<img
						src={commarecShot}
						alt="Platforma Commarec"
						class="block aspect-[1440/900] w-full object-cover object-top"
						width="1280"
						height="800"
					/>
				</div>
				<span
					class="absolute -right-3 top-8 flex items-center gap-2 rounded-full bg-fstgreen px-4 py-2 text-sm font-medium text-fstonyx shadow-lg max-lg:hidden"
				>
					<span class="flex h-5 w-5 items-center justify-center rounded-full bg-fstonyx text-xs text-fstgreen">+</span>
					Vlastní CMS
				</span>
				<span
					class="absolute -left-3 top-1/2 flex items-center gap-2 rounded-full bg-fstgreen px-4 py-2 text-sm font-medium text-fstonyx shadow-lg max-lg:hidden"
				>
					<span class="flex h-5 w-5 items-center justify-center rounded-full bg-fstonyx text-xs text-fstgreen">+</span>
					Obsahový hub
				</span>
				<span
					class="absolute -right-3 bottom-8 flex items-center gap-2 rounded-full bg-fstgreen px-4 py-2 text-sm font-medium text-fstonyx shadow-lg max-lg:hidden"
				>
					<span class="flex h-5 w-5 items-center justify-center rounded-full bg-fstonyx text-xs text-fstgreen">+</span>
					Roste s firmou
				</span>
			</div>
		</div>
	</Section>

	<!-- Statistiky -->
	<Section background={Background.Onyx}>
		<div class="container-12 grid grid-cols-2 gap-8 text-white lg:grid-cols-4 lg:gap-12">
			{#each [{ n: "5 měsíců", d: "od zadání ke spuštění platformy" }, { n: "1 CMS", d: "pro správu veškerého obsahu bez závislosti na vývojářích" }, { n: "3 v 1", d: "marketing, obsah a komunita na jednom místě" }, { n: "100 %", d: "custom řešení — žádná šablona ani krabice" }] as stat (stat.d)}
				<div>
					<p class="mb-2 font-loos-wide text-[2rem] font-black leading-none text-fstgreen lg:text-[2.5rem]">
						{stat.n}
					</p>
					<p class="text-sm leading-snug text-fstgrey-300">{stat.d}</p>
				</div>
			{/each}
		</div>
	</Section>

	<!-- O klientovi -->
	<Section id="pribeh" background={Background.White} gridChildren="container-12">
		<div class="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
			<div>
				<span class="mb-4 block font-loos-normal text-xs font-medium uppercase tracking-[0.34em] text-fstgrey-200">
					O klientovi
				</span>
				<h2 class="!mb-6">Firma, která chtěla obsahový hub pro celou logistiku</h2>
				<p class="max-w-xl text-md leading-[1.5em]">
					<Formatted
						text="[https://www.commarec.cz][[Commarec]] se zaměřuje na technologická řešení pro logistiku a skladování. Jejich ambicí je popularizovat moderní logistiku, sdílet odborné zkušenosti a propojovat profesionály z oboru — produkty, inspiraci i komunitu na jednom místě."
					/>
				</p>
				<p class="max-w-xl text-md italic leading-[1.5em] text-fstgrey-500">
					<Formatted
						text="„Nechtěli jsme web, kde napíšeme jen to, co děláme. Chtěli jsme platformu, která bude s námi moct růst.&quot;"
					/>
				</p>
			</div>

			<div class="rounded-[1.25rem] border-4 border-fstlight-100 bg-fstlight-100 p-6 md:p-8">
				<span class="mb-6 block font-loos-normal text-xs font-medium uppercase tracking-[0.34em] text-fstgrey-300">
					Klíčové informace o zakázce
				</span>
				<dl class="space-y-4">
					{#each [{ t: "Klient", d: "Commarec — technologie pro logistiku a skladování" }, { t: "Typ projektu", d: "Digitální platforma na míru s vlastním CMS" }, { t: "Období realizace", d: "08/2025 – 01/2026 · spolupráce pokračuje doposud" }, { t: "Technologie", d: "AWS, Svelte, DynamoDB, AppSync, GraphQL, Tailwind" }] as row (row.t)}
						<div class="border-b border-fstlight-300 pb-4 last:border-0 last:pb-0">
							<dt class="mb-1 text-sm text-fstgrey-300">{row.t}</dt>
							<dd class="text-base font-medium text-fstonyx">{row.d}</dd>
						</div>
					{/each}
				</dl>
				<div class="mt-6 flex flex-wrap gap-2">
					{#each [{ label: "Logistika", href: getTransportPath(tenant) }, { label: "Obsahová platforma", href: getWebsitesPath(tenant) }, { label: "Digitalizace", href: getDigitalizationAndAutomationPath(tenant) }, { label: "Integrace", href: getIntegrationsPath(tenant) }] as tag (tag.label)}
						<a
							href={tag.href}
							class="rounded-full bg-white px-3 py-1.5 text-xs font-medium text-fstgrey-300 transition-colors hover:text-fstgreen"
							>{tag.label}</a
						>
					{/each}
				</div>
			</div>
		</div>
	</Section>

	<!-- Výzvy -->
	<Section background={Background.Light} gridChildren="container-12">
		<span class="mb-4 block font-loos-normal text-xs font-medium uppercase tracking-[0.34em] text-fstgrey-200">
			Výzva
		</span>
		<h2 class="!mb-6">Postavit platformu, ne další firemní web</h2>
		<p class="max-w-3xl text-md leading-[1.5em]">
			Cílem nebyl „jen web", ale obsahový ekosystém pro logistický obor. Zakázka stála na třech klíčových výzvách.
		</p>

		<div class="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
			{#each challenges as item (item.name)}
				<div class={["rounded-[1.25rem] p-8", item.featured ? "bg-fstonyx" : "border-4 border-fstlight-100 bg-white"]}>
					<div
						class={[
							"mb-6 flex h-14 w-14 items-center justify-center rounded-[14px]",
							item.featured ? "bg-fstgreen text-fstonyx" : "bg-fstlight-100 text-fstgreen",
						]}
					>
						<Icon icon={item.icon} class="w-7" square />
					</div>
					<span
						class={[
							"mb-2 block font-loos-normal text-xs font-medium uppercase tracking-[0.25em]",
							item.featured ? "text-fstgreen" : "text-fstgrey-300",
						]}
					>
						{item.label}
					</span>
					<h3 class={["!mb-2 !mt-0 text-md font-black", item.featured ? "text-white" : "text-fstonyx"]}>
						{item.name}
					</h3>
					<p class={["!mb-0 text-sm leading-snug", item.featured ? "text-fstgrey-300" : "text-fstgrey-200"]}>
						{item.desc}
					</p>
				</div>
			{/each}
		</div>
	</Section>

	<!-- CTA 2 -->
	<Section background={Background.Light} noPadding="top">
		{@render ctaInline(
			codeIcon,
			"Chcete web, který ve vašem oboru vyčnívá?",
			"Navrhujeme weby a platformy na míru — bez šablon, podle toho, kdo jste a kam míříte.",
			"Jak stavíme weby na míru",
			getWebsitesPath(tenant),
		)}
	</Section>

	<!-- Řešení -->
	<Section background={Background.White} gridChildren="container-12">
		<span class="mb-4 block font-loos-normal text-xs font-medium uppercase tracking-[0.34em] text-fstgrey-200">
			Řešení
		</span>
		<h2 class="!mb-6">Web na míru s vlastním CMS</h2>
		<p class="max-w-3xl text-md leading-[1.5em]">
			Plně custom webová platforma s maximální flexibilitou. Tým Commarecu může rychle publikovat obsah bez technických
			omezení a bez závislosti na vývojářích — CMS je navržen přesně pro jejich procesy.
		</p>

		<div class="mt-10">
			<h3 class="!mb-2 text-base font-bold text-fstonyx md:text-md">Obsahový hub pro logistiku</h3>
			<p class="mb-8 text-sm text-fstgrey-300">články · případovky · podcasty · produkty · komunita · inspirace</p>
			<div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
				{#each cmsFeatures as feature (feature.label)}
					<div class="flex items-center gap-4 rounded-[14px] bg-fstlight-100 px-5 py-4">
						<Icon icon={feature.icon} class="w-6 shrink-0 text-fstgreen" square />
						<span class="text-base font-medium text-fstonyx">{feature.label}</span>
					</div>
				{/each}
			</div>
		</div>

		<div class="mt-12 overflow-hidden rounded-[1.25rem] bg-fstlight-100">
			<div class="flex items-center gap-2 border-b border-fstlight-200 px-4 py-3">
				<span class="h-3 w-3 rounded-full bg-fstlight-300"></span>
				<span class="h-3 w-3 rounded-full bg-fstlight-300"></span>
				<span class="h-3 w-3 rounded-full bg-fstlight-300"></span>
				<span class="ml-3 flex-1 rounded-full bg-white px-4 py-1 text-xs text-fstgrey-300">commarec.cz</span>
			</div>
			<img src={commarecShot} alt="Platforma Commarec" class="block w-full" width="1280" height="800" />
		</div>
	</Section>

	<!-- CTA 3 -->
	<Section background={Background.Light}>
		{@render ctaInline(
			databaseIcon,
			"Potřebuje váš tým spravovat obsah bez závislosti na vývojářích?",
			"Navrhneme vlastní CMS podle vašich procesů — publikujte přesně tak, jak vám to vyhovuje.",
			"Jak vyvíjíme webové aplikace na míru",
			getWebAppsPath(tenant),
		)}
	</Section>

	<!-- Tmavá citace -->
	<Section background={Background.Onyx} centered>
		<div class="mx-auto max-w-3xl text-center">
			<Icon icon={quoteIcon} class="mx-auto mb-6 h-12 w-12 text-fstgreen" square />
			<p class="mx-auto max-w-3xl text-lg font-black leading-tight text-white md:text-xl lg:text-2xl">
				S Martinem Kučerou jsme zahájili spolupráci v srpnu 2025 a v lednu 2026 jsme úspěšně spustili nový web.
				<span class="text-fstgreen">Spolupráce byla od začátku velmi transparentní.</span>
			</p>
			<div class="mt-8">
				<p class="text-base font-bold text-white">Martin Komárek</p>
				<p class="text-sm text-fstgrey-300">CEO Commarec</p>
			</div>
		</div>
	</Section>

	<!-- CTA 4 -->
	<Section background={Background.Light}>
		{@render ctaBig(
			bracketsIcon,
			"Přemýšlíte o platformě, ne jen o webu?",
			"Firemní web může být víc než vizitka.",
			"Ukážeme vám, jak vybudovat digitální platformu, která roste s vaším byznysem a stává se místem pro komunitu, obsah i zákazníky.",
			"Chci probrat vlastní platformu",
			getConnectWithFastestPath(tenant),
		)}
	</Section>

	<!-- Výsledek -->
	<Section background={Background.White} gridChildren="container-12">
		<div class="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center lg:gap-16">
			<div>
				<span class="mb-4 block font-loos-normal text-xs font-medium uppercase tracking-[0.34em] text-fstgrey-200">
					Výsledek
				</span>
				<p class="!mb-3 font-loos-wide text-[4rem] font-black leading-none text-fstgreen md:text-[5rem] lg:text-[6rem]">
					3 v 1
				</p>
				<p class="text-lg font-medium text-fstonyx">marketing, obsah a komunita</p>
			</div>
			<div>
				<h2 class="!mb-6">Platforma, která buduje pozici experta v logistice</h2>
				<p class="text-md leading-[1.5em]">
					Za pět měsíců vývoje vznikla platforma kombinující marketing, obsah a komunitu. Web dnes prezentuje služby a
					produkty Commarecu, buduje jejich značku jako experta v logistice a umožňuje dlouhodobě publikovat odborný
					obsah.
				</p>
				<p class="text-md leading-[1.5em]">
					Projekt spuštěním webu nekončí — platforma je navržena pro další rozvoj a postupné přidávání nových funkcí
					podle toho, jak firma roste.
				</p>
				<ul class="mt-6 space-y-3">
					{#each ["Spuštěno v lednu 2026", "Spolupráce pokračuje doposud"] as item (item)}
						<li class="flex items-center gap-3 font-medium text-fstonyx">
							<span
								class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-fstgreen text-xs text-white"
							>
								✓
							</span>
							{item}
						</li>
					{/each}
				</ul>
			</div>
		</div>
	</Section>

	<!-- CTA 5 -->
	<Section background={Background.Light}>
		{@render ctaBig(
			gitIcon,
			"Hledáte partnera na dlouhodobý rozvoj?",
			"Nejsme agentura na jednorázové projekty.",
			"Navrhneme platformu, která začíná tam, kde vás teď tlačí bota, a roste dál podle vašich potřeb — i po spuštění.",
			"Chci probrat vlastní projekt",
			getConnectWithFastestPath(tenant),
		)}
	</Section>

	<!-- Technologie -->
	<Section background={Background.Light} centered gridHeadline="container-10" noPadding="top">
		{#snippet headline()}
			<span class="mb-4 block font-loos-normal text-xs font-medium uppercase tracking-[0.34em] text-fstgrey-200">
				Technologie
			</span>
			Postaveno na moderním cloud-native stacku
		{/snippet}
		<div class="container-12 flex flex-wrap justify-center gap-3">
			{#each technologies as tech (tech)}
				<span
					class="flex items-center gap-2 rounded-full border-2 border-fstlight-300 bg-white px-5 py-2.5 text-base font-medium text-fstonyx before:h-1.5 before:w-1.5 before:shrink-0 before:rounded-full before:bg-fstgreen before:content-['']"
				>
					{tech}
				</span>
			{/each}
		</div>
	</Section>

	<!-- Spodní banner -->
	<Section background={Background.Onyx} gridChildren="container-12">
		<div class="grid grid-cols-1 items-center gap-12 lg:grid-cols-[3fr_2fr] lg:gap-20">
			<div>
				<span class="mb-6 block font-loos-normal text-xs font-medium uppercase tracking-[0.34em] text-fstgreen">
					Pojďme to probrat
				</span>
				<h2 class="!mb-6 text-white">Chcete také platformu, která roste s vaším byznysem?</h2>
				<p class="mb-8 text-md leading-[1.5em] text-fstgrey-300">
					Podíváme se na vaše cíle, obsah, produkty i komunitu. Navrhneme řešení, které přináší dlouhodobou hodnotu a
					rozvíjí se spolu s vámi.
				</p>
				<ul class="grid grid-cols-1 gap-3 sm:grid-cols-2">
					{#each bannerLinks as link (link.label)}
						<li>
							<a
								href={link.href}
								class="flex items-center gap-2 text-base text-fstgrey-300 transition-colors hover:text-fstgreen"
							>
								<span class="text-fstgreen">→</span>
								{link.label}
							</a>
						</li>
					{/each}
				</ul>
			</div>

			<div
				class="w-full rounded-t-[1.25rem] rounded-b-[1.5rem] bg-fstgrey-800 px-[2.125rem] pt-[2.125rem] pb-[2.25rem] flex flex-col gap-5"
			>
				<div class="flex items-center gap-5">
					{#if martin.image}
						<div class="shrink-0 rounded-full border-4 border-fstgreen p-[3px]">
							<Media
								source={martin.image}
								alt={martin.name}
								width={96}
								height={96}
								class="h-24 w-24 rounded-full object-cover"
							/>
						</div>
					{/if}
					<div>
						<p class="text-[1.5rem] font-bold leading-tight text-white">{martin.name}</p>
						<p class="mt-1 text-sm font-medium text-fstgreen">CEO · Solution Owner</p>
					</div>
				</div>
				<p class="text-sm leading-[1.375rem] text-fstlight-300">
					Přebírá odpovědnost za pochopení vašeho byznysu, návrh řešení i dodání funkčního výsledku.
				</p>
				<div class="flex flex-col gap-3">
					{#if martin.phone}
						<a
							href="tel:{martin.phone}"
							class="flex items-center gap-4 text-white transition-colors hover:text-fstgreen"
						>
							<span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-fstgrey-900">
								<Icon icon={phoneIcon} class="w-5 text-fstgreen" square />
							</span>
							<span class="text-base font-medium">{martin.phone}</span>
						</a>
					{/if}
					{#if martin.email}
						<a
							href="mailto:{martin.email}"
							class="flex items-center gap-4 text-white transition-colors hover:text-fstgreen"
						>
							<span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-fstgrey-900">
								<Icon icon={envelopeIcon} class="w-5 text-fstgreen" square />
							</span>
							<span class="text-base font-medium">{martin.email}</span>
						</a>
					{/if}
				</div>
				<Link
					variant={ButtonVariant.Phlox}
					class="flex w-full items-center justify-center"
					href={getConnectWithFastestPath(tenant)}
				>
					Domluvit schůzku
					<Icon icon={arrowNext} class="ml-2 shrink-0 text-fstgrey-900"></Icon>
				</Link>
			</div>
		</div>
	</Section>

	<!-- Sticky floating CTA -->
	<div
		class={[
			"fixed bottom-6 left-1/2 z-40 -translate-x-1/2 transition-all duration-300 max-md:hidden",
			showStickyCta ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-4 opacity-0",
		]}
	>
		<div class="flex items-center gap-4 rounded-full bg-fstonyx py-2 pl-6 pr-2 shadow-[0_8px_30px_rgba(33,32,32,0.25)]">
			<span class="flex items-center gap-2 text-sm font-medium text-white">
				<span class="h-2 w-2 rounded-full bg-fstgreen"></span>
				Přemýšlíte o vlastní platformě?
			</span>
			<Link
				variant={ButtonVariant.Phlox}
				class="flex !w-auto items-center justify-center !py-2.5"
				href={getConnectWithFastestPath(tenant)}
			>
				Probrat můj projekt
				<Icon icon={arrowNext} class="ml-2 shrink-0 text-fstgrey-900"></Icon>
			</Link>
		</div>
	</div>
</Layout>
